"use client";

import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Phone,
  Calendar,
  ClipboardCheck,
  Bot,
  GitBranch,
  MessageSquare,
  Settings,
  FileText,
  Menu,
  X,
  PanelLeftClose,
  Search,
  Plug,
  FlaskConical,
  Bell,
  ScrollText,
  Database,
  BookOpen,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useSidebar } from "./SidebarProvider";
import { useSidebarCounts } from "@/hooks/useSidebarCounts";

type BadgeKey = "leads" | "appointments" | "followUps";
type NavItem = { href: string; label: string; icon: typeof LayoutDashboard; badgeKey?: BadgeKey };
type NavSection = { section?: string; items: NavItem[] };

const NAV_SECTIONS: Record<string, NavSection[]> = {
  admin: [
    {
      items: [
        { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      ],
    },
    {
      section: "Operations",
      items: [
        { href: "/leads", label: "All Leads", icon: Phone, badgeKey: "leads" },
        { href: "/appointments", label: "Appointments", icon: Calendar, badgeKey: "appointments" },
        { href: "/follow-ups", label: "Follow-ups", icon: ClipboardCheck, badgeKey: "followUps" },
      ],
    },
    {
      section: "Communications",
      items: [
        { href: "/communications/templates", label: "Templates", icon: MessageSquare },
        { href: "/communications/providers", label: "Providers", icon: Plug },
        { href: "/communications/notifications", label: "Notifications", icon: Bell },
      ],
    },
    {
      section: "Configuration",
      items: [
        { href: "/admin/config/qualification", label: "Qualification", icon: ClipboardCheck },
        { href: "/admin/config/call-script", label: "Call Script", icon: ScrollText },
        { href: "/admin/config/client-database", label: "Client Database", icon: Database },
        { href: "/admin/config", label: "Settings", icon: Settings },
      ],
    },
    {
      section: "System",
      items: [
        { href: "/admin/users", label: "Users", icon: Users },
        { href: "/admin/agents", label: "AI Agents", icon: Bot },
        { href: "/admin/assignment-rules", label: "Assignment Rules", icon: GitBranch },
        { href: "/admin/test-lead", label: "Test Lead", icon: FlaskConical },
        { href: "/admin/audit", label: "Audit Log", icon: FileText },
      ],
    },
    {
      section: "",
      items: [
        { href: "/docs", label: "User Guide", icon: BookOpen },
      ],
    },
  ],
  telemarketer: [
    {
      items: [
        { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { href: "/leads", label: "My Leads", icon: Phone, badgeKey: "leads" },
        { href: "/follow-ups", label: "Follow-ups", icon: ClipboardCheck, badgeKey: "followUps" },
      ],
    },
    {
      section: "",
      items: [
        { href: "/docs", label: "User Guide", icon: BookOpen },
      ],
    },
  ],
  advisor: [
    {
      items: [
        { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { href: "/appointments", label: "My Appointments", icon: Calendar, badgeKey: "appointments" },
        { href: "/follow-ups", label: "Follow-ups", icon: ClipboardCheck, badgeKey: "followUps" },
      ],
    },
    {
      section: "",
      items: [
        { href: "/docs", label: "User Guide", icon: BookOpen },
      ],
    },
  ],
  bsc: [
    {
      items: [
        { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { href: "/appointments", label: "Appointments", icon: Calendar, badgeKey: "appointments" },
        { href: "/follow-ups", label: "Follow-ups", icon: ClipboardCheck, badgeKey: "followUps" },
      ],
    },
    {
      section: "",
      items: [
        { href: "/docs", label: "User Guide", icon: BookOpen },
      ],
    },
  ],
};

export function Sidebar() {
  const pathname = useLocation().pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebar();
  const [role, setRole] = useState("admin");

  const navigate = useNavigate();
  const sidebarCounts = useSidebarCounts();
  const [impersonating, setImpersonating] = useState<{ name: string | null; role: string } | null>(null);

  // Fetch current role from session (production) or dev cookie
  useEffect(() => {
    // Check impersonation cookie first
    const impMatch = document.cookie.match(/impersonate-user=([^;]+)/);
    if (impMatch) {
      try {
        const parsed = JSON.parse(decodeURIComponent(impMatch[1]));
        setImpersonating({ name: parsed.name, role: parsed.role });
        setRole(parsed.role);
        return;
      } catch {
        // Invalid cookie — ignore
      }
    }

    if (process.env.NODE_ENV === "development") {
      const match = document.cookie.match(/dev-role=(\w+)/);
      if (match) setRole(match[1]);
    } else {
      // Production: get role from session
      fetch("/api/auth/session", { credentials: "include" })
        .then((r) => r.json())
        .then((data) => {
          const r = data?.user?.role;
          if (r) setRole(r);
        })
        .catch(() => {});
    }
  }, []);

  async function switchDevRole(newRole: string) {
    setRole(newRole);
    document.cookie = `dev-role=${newRole};path=/`;
    await fetch("/api/dev/switch-role", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: newRole }),
    });
    window.dispatchEvent(new Event("dev-role-changed"));
    window.location.reload();
  }

  const navSections = NAV_SECTIONS[role] ?? NAV_SECTIONS.admin;

  const navContent = (
    <>
      {/* Brand */}
      <div className={cn("flex items-center gap-3 px-4 py-4", collapsed && "justify-center px-2")}>
        <img
          src="/aluma-logo.png"
          alt="Aluma"
          width={32}
          height={32}
          className="h-8 w-8 shrink-0"
        />
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white">ALUMA</div>
              <div className="text-[11px] text-gray-400">CRM Platform</div>
            </div>
            <button
              onClick={toggleCollapsed}
              className="hidden shrink-0 rounded p-1 text-gray-500 transition-colors hover:bg-[#1a2e4a] hover:text-white lg:flex"
              title="Collapse sidebar"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="px-3 pb-2">
          <Link
            to="/leads"
            className="flex items-center gap-2 rounded-lg bg-[#1a2e4a] px-3 py-2 text-sm text-gray-400 transition-colors hover:text-white"
          >
            <Search className="h-4 w-4" />
            <span>Search leads...</span>
          </Link>
        </div>
      )}

      {/* Navigation */}
      <nav className={cn("flex-1 overflow-y-auto px-3", collapsed && "px-2")}>
        {navSections.map((section, sIdx) => (
          <div key={sIdx} className={sIdx > 0 ? "mt-3" : ""}>
            {section.section && !collapsed && (
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                {section.section}
              </p>
            )}
            {section.section && collapsed && sIdx > 0 && (
              <div className="mx-2 mb-2 border-t border-[#1a2e4a]" />
            )}
            <div className="space-y-0.5">
              {section.items.map(({ href, label, icon: Icon, badgeKey }) => {
                const isActive = href === "/dashboard" ? pathname === "/dashboard" : pathname.startsWith(href);
                const badgeCount = badgeKey && sidebarCounts ? sidebarCounts[badgeKey] : 0;
                return (
                  <Link
                    key={href}
                    to={href}
                    onClick={() => setMobileOpen(false)}
                    title={collapsed ? label : undefined}
                    className={cn(
                      "relative flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                      collapsed && "justify-center px-0",
                      isActive
                        ? "bg-[#1e5bb5]/20 text-white"
                        : "text-gray-400 hover:bg-[#1a2e4a] hover:text-white",
                    )}
                  >
                    <span className="relative shrink-0">
                      <Icon className="h-4 w-4" />
                      {collapsed && badgeCount > 0 && (
                        <span className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-red-500" />
                      )}
                    </span>
                    {!collapsed && label}
                    {!collapsed && badgeCount > 0 && (
                      <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom section — pinned */}
      <div className="shrink-0">
        {/* Logout */}
        {!collapsed && (
          <div className="border-t border-[#1a2e4a] px-3 py-2">
            <button
              onClick={async () => {
                await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
                navigate("/login", { replace: true });
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-400 transition-colors hover:bg-[#1a2e4a] hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        )}

        {/* Impersonation indicator */}
        {impersonating && !collapsed && (
          <div className="border-t border-amber-500/30 bg-amber-500/10 p-3">
            <div className="text-center text-[11px] font-medium text-amber-400">
              Viewing as: {impersonating.name ?? "Unknown"}
            </div>
          </div>
        )}

        {/* Demo data toggle (admin only, all environments) */}
        {role === "admin" && !collapsed && (
          <div className={cn("border-t border-[#1a2e4a] p-3", impersonating && "border-t-0")}>
            <DemoDataToggle />
          </div>
        )}

        {/* Role switcher (dev only) */}
        {process.env.NODE_ENV === "development" && !collapsed && !impersonating && (
          <div className="border-t border-[#1a2e4a] p-3">
            <DevRoleSwitcher currentRole={role} onSwitch={switchDevRole} />
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed left-4 top-4 z-50 rounded-lg bg-[#0f1f33] p-2 text-gray-400 lg:hidden"
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        onClick={(e) => {
          if (!collapsed) return;
          const target = e.target as HTMLElement;
          if (target.closest("a")) return;
          toggleCollapsed();
        }}
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex flex-col border-r border-[#1a2e4a] bg-[#0f1f33] transition-all duration-200",
          "lg:relative lg:translate-x-0",
          mobileOpen ? "w-64 translate-x-0" : "-translate-x-full",
          collapsed ? "lg:w-16 lg:cursor-pointer" : "lg:w-64",
        )}
      >
        {navContent}
      </aside>
    </>
  );
}

const DEV_ROLES = ["admin", "telemarketer", "advisor", "bsc"] as const;

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function DemoDataToggle() {
  const [demoOn, setDemoOn] = useState(() => {
    const cookie = getCookie("demo-data");
    // Default to "on" if no cookie set yet
    if (cookie === null) {
      document.cookie = "demo-data=on;path=/";
      return true;
    }
    return cookie === "on";
  });

  function toggleDemo() {
    const next = !demoOn;
    setDemoOn(next);
    document.cookie = `demo-data=${next ? "on" : "off"};path=/`;
    window.dispatchEvent(new Event("demo-data-changed"));
  }

  return (
    <button
      onClick={toggleDemo}
      className={cn(
        "flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs font-medium transition-colors",
        demoOn
          ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
          : "bg-[#1a2e4a] text-gray-400 hover:text-white",
      )}
    >
      <span>Demo Data</span>
      <div
        className={cn(
          "relative h-4 w-8 rounded-full transition-colors",
          demoOn ? "bg-green-500" : "bg-gray-600",
        )}
      >
        <div
          className={cn(
            "absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform",
            demoOn ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </div>
    </button>
  );
}

function DevRoleSwitcher({ currentRole, onSwitch }: { currentRole: string; onSwitch: (role: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full rounded-lg bg-amber-500/20 px-3 py-2 text-center text-xs font-medium text-amber-400 transition-colors hover:bg-amber-500/30"
      >
        Role: <span className="capitalize">{currentRole}</span>
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-full rounded-lg border border-[#1a2e4a] bg-[#0f1f33] p-1 shadow-lg">
          {DEV_ROLES.map((r) => (
            <button
              key={r}
              onClick={() => { onSwitch(r); setOpen(false); }}
              className={cn(
                "w-full rounded px-3 py-1.5 text-left text-xs capitalize transition-colors",
                r === currentRole
                  ? "bg-amber-500/20 font-medium text-amber-400"
                  : "text-gray-400 hover:bg-[#1a2e4a] hover:text-white",
              )}
            >
              {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
